import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../components/LoginModal';

const FavoritesContext = createContext();

export const useFavorites = () => {
    return useContext(FavoritesContext);
};

export const FavoritesProvider = ({ children }) => {
    const [favoriteIds, setFavoriteIds] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingFavorite, setPendingFavorite] = useState(null);
    const navigate = useNavigate();

    const fetchFavorites = useCallback(async () => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            setFavoriteIds([]);
            return;
        }

        setIsFetching(true);
        try {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            const res = await fetch(`${apiBase}/auth/favorites`, {
                headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                if (data.success && data.favorite_ids) {
                    setFavoriteIds(data.favorite_ids);
                }
            } else if (res.status === 401) {
                setFavoriteIds([]);
            }
        } catch (error) {
            console.error('Failed to fetch favorites', error);
        } finally {
            setIsFetching(false);
        }
    }, []);

    // Clear local storage and fetch favorites when the provider mounts
    useEffect(() => {
        localStorage.removeItem('zoyotrip_saved_hotels');
        fetchFavorites();
    }, [fetchFavorites]);

    const toggleFavorite = async (propertyId) => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            setPendingFavorite(propertyId);
            setShowLoginModal(true);
            return false;
        }

        // Optimistic UI update
        const isCurrentlyFavorited = favoriteIds.includes(propertyId);
        const newIds = isCurrentlyFavorited 
            ? favoriteIds.filter(id => id !== propertyId) 
            : [...favoriteIds, propertyId];
        
        setFavoriteIds(newIds);

        try {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            const res = await fetch(`${apiBase}/auth/favorites/toggle`, {
                method: 'POST',
                headers: { 
                    'Accept': 'application/json', 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ property_id: propertyId })
            });

            if (res.ok) {
                const data = await res.json();
                if (data.success && data.favorite_ids) {
                    setFavoriteIds(data.favorite_ids); // Sync with source of truth
                }
            } else {
                // Revert optimistic update on failure
                setFavoriteIds(favoriteIds);
                if (res.status === 401) {
                    navigate('/login');
                }
            }
        } catch (error) {
            console.error('Failed to toggle favorite', error);
            setFavoriteIds(favoriteIds); // Revert
        }
        return true;
    };

    const handleLoginSuccess = async () => {
        setShowLoginModal(false);
        await fetchFavorites();
        if (pendingFavorite) {
            // Give a slight delay for token to be available
            setTimeout(() => {
                toggleFavorite(pendingFavorite);
                setPendingFavorite(null);
            }, 100);
        }
    };

    return (
        <FavoritesContext.Provider value={{ favoriteIds, toggleFavorite, isFetching, refreshFavorites: fetchFavorites }}>
            {children}
            <LoginModal 
                isOpen={showLoginModal} 
                onClose={() => {
                    setShowLoginModal(false);
                    setPendingFavorite(null);
                }}
                onSuccess={handleLoginSuccess}
            />
        </FavoritesContext.Provider>
    );
};
